'use client';

import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import moment from 'moment';
import AddBtn from '../../../components/common/AddBtn';
import Pagination from '../../../components/common/Pagination';
import Spinner from '../../../components/layout/Spinner';
import FilterDateComponent from '../../../components/common/FilterDateComponent';
import ExportComponent from '../../../components/common/ExportComponent';
import {
  useAllActivitysByEmployee,
  useGetDropdownOptions,
  useUpdateActivity,
} from '../../../shared/hooks/UseActivity';
import {
  useAllComments,
  useCreateComment,
} from '../../../shared/hooks/UseComment';
import { LIMIT } from '../../../domain/constant';
import { UseDataForExcel, UseFilter } from '../../../shared/hooks/UseExcel';
import { useSelector } from 'react-redux';
import { useHistory, useLocation } from 'react-router';
import * as qs from 'qs';

const PAGE_SINGLE_TITLE = 'Activity';
const LINK_URL = 'activitys';

const ActivityTableView = ({ employee }) => {
  const [data, setPageNumber, deleteBtnClicked, reloadData, setReloadData] =
    useAllActivitysByEmployee(employee._id);
  const [updateData] = useUpdateActivity();

  const {
    activitys_loading,
    activitys,
    total_activitys,
    activities_status_array,
    page,
    pages,
  } = data;

  const [comment_data, setActivity, setReloadComments] = useAllComments();
  const { comments, comments_loading } = comment_data;
  const [commentData, addData] = useCreateComment();

  const [dropdownOptions, ...dropdownHooks] = useGetDropdownOptions();
  const [modal, setModal] = useState(false);
  const [modalData, setModalData] = useState(null);
  const [editId, setEditId] = useState(null);
  const [editField, setEditField] = useState(null);
  const [newValues, setNewValues] = useState(null);
  const [loadingActivityStatus, setLoadingActivityStatus] = useState(false);

  const [total_estimated_time, setTotal_estimated_time] = useState(0);
  const [total_actual_time, setTotal_actual_time] = useState(0);

  const [exportXLSXData] = UseFilter();
  const [convertToReadable, exportData] = UseDataForExcel();
  const userData = useSelector((state) => state.auth);
  const { user } = userData;
  const history = useHistory();
  const location = useLocation();

  // Time conversion function
  const time_convert = (time) => {
    const hours = Math.floor(time / 60);
    const minutes = time % 60;
    return `${hours}h ${minutes}m`;
  };

  // Calculate time totals
  useEffect(() => {
    if (activitys) {
      let total_activity_time = 0;
      let total_actual_time = 0;
      activitys.forEach((item) => {
        if (item.estimated_time) {
          total_activity_time += Number(item.estimated_time || 0);
          total_actual_time += Number(item.actual_time || 0);
        }
      });
      setTotal_estimated_time(total_activity_time);
      setTotal_actual_time(total_actual_time);
    }
  }, [activitys]);

  // Export functionality
  useEffect(() => {
    if (activitys) {
      convertToReadable(activitys, {});
    }
  }, [activitys]);

  const handleOnExport = () => {
    exportXLSXData(exportData, 'Activities', 'activities');
  };

  const submitFormClicked = async (values) => {
    await addData(values);
    setReloadComments(true);
  };

  const updateStatus = async (id, status) => {
    if (id && status) {
      setLoadingActivityStatus(true);
      const newForm = new FormData();
      newForm.append('values', JSON.stringify(status));
      await updateData(id, newForm);
      await setReloadData(true);
      setEditId(null);
      setLoadingActivityStatus(false);
    } else {
      setEditId(null);
    }
  };

  const handleSidebarChange = ({ name, value, type = 'search', condition }) => {
    const queryParams = qs.parse(window.location.search.replace('?', ''));
    if (type === 'conditional') {
      if (queryParams[type]) {
        if (queryParams[type][name]) {
          if (value && value.length > 0) {
            queryParams[type][name][condition || '$gte'] = value;
          } else {
            delete queryParams[type][name][condition || '$gte'];
          }
        } else {
          queryParams[type][name] = {};
          if (value && value.length > 0) {
            queryParams[type][name][condition || '$gte'] = value;
          } else {
            delete queryParams[type][name][condition || '$gte'];
          }
        }
      } else {
        queryParams[type] = {};
        queryParams[type][name] = {};
        if (value && value.length > 0) {
          queryParams[type][name][condition || '$gte'] = value;
        } else {
          delete queryParams[type][name][condition || '$gte'];
        }
      }
    } else {
      if (queryParams[type]) {
        if (value && value.length > 0) {
          queryParams[type][name] = value;
        } else {
          delete queryParams[type][name];
        }
      } else {
        queryParams[type] = {};
        if (value && value.length > 0) {
          queryParams[type][name] = value;
        } else {
          delete queryParams[type][name];
        }
      }
    }

    const query = qs.stringify(queryParams, { encodeValuesOnly: true });
    history.push(`${location.pathname}?${query}`);
  };

  return (
    <>
      <div className='enhanced-activity-container'>
        <ExportComponent handleOnExport={handleOnExport} />
        <FilterDateComponent
          link={`/employees/${employee?._id}/view`}
          searchField='due_time'
        />

        <div className='enhanced-activity-content'>
          <div className='enhanced-activity-card'>
            {/* Status Filter Cards */}
            <div className='enhanced-status-filters'>
              <div className='enhanced-filter-left'>
                <AddBtn item={LINK_URL} title={PAGE_SINGLE_TITLE} />
              </div>
              <div className='enhanced-filter-cards'>
                {dropdownOptions &&
                  dropdownOptions.activity_status &&
                  dropdownOptions.activity_status.map((item) => (
                    <div key={item.value} className='enhanced-status-card'>
                      <div className='enhanced-status-card-body'>
                        <h4 className='enhanced-status-title'>
                          <a
                            onClick={() =>
                              handleSidebarChange({
                                name: 'activity_status',
                                value: item.value,
                                type: item.search_type
                                  ? item.search_type
                                  : 'exact',
                              })
                            }
                          >
                            {item.label}
                          </a>
                        </h4>
                      </div>
                    </div>
                  ))}
              </div>
            </div>

            <div className='enhanced-divider'></div>

            {/* Action Bar with Time Metrics */}
            <div className='enhanced-action-bar'>
              <div className='enhanced-action-left'>
                <Link
                  className='enhanced-btn enhanced-btn-danger'
                  to={`/activitys?conditional[due_time][$lt]=${moment().format(
                    'YYYY-MM-DD'
                  )}&exact[activity_status]=62b2a7abdb1f21d46398f0b8`}
                >
                  <i className='fa fa-exclamation-triangle me-1'></i>
                  Overdue Tasks
                </Link>
              </div>

              <div className='enhanced-time-metrics'>
                <div className='enhanced-metric-card'>
                  <div className='enhanced-metric-body'>
                    <h4 className='enhanced-metric-title'>
                      Total Estimated Time
                    </h4>
                    <div className='enhanced-metric-value'>
                      {time_convert(total_estimated_time)}
                    </div>
                  </div>
                </div>
                <div className='enhanced-metric-card'>
                  <div className='enhanced-metric-body'>
                    <h4 className='enhanced-metric-title'>Total Actual Time</h4>
                    <div className='enhanced-metric-value'>
                      {time_convert(total_actual_time)}
                    </div>
                  </div>
                </div>
              </div>

              <div className='enhanced-action-right'>
                <Link
                  to='/activitys/bulk-add'
                  className='enhanced-btn enhanced-btn-secondary'
                >
                  <i className='fa fa-upload me-1'></i>
                  Bulk Add
                </Link>
              </div>
            </div>

            {/* Records Count */}
            <div className='enhanced-records-info'>
              <i className='fa fa-list me-1'></i>
              {total_activitys} records found
            </div>

            {/* Enhanced Table */}
            <div className='enhanced-table-container'>
              <table className='enhanced-table'>
                {!activitys_loading && (
                  <thead className='enhanced-table-header'>
                    <tr>
                      <th style={{ width: '50px' }}>#</th>
                      <th>Task Name</th>
                      <th>Assigned To</th>
                      <th>Due Time</th>
                      <th>Est. Time</th>
                      <th>Act. Time</th>
                      <th>Client</th>
                      <th>Process</th>
                      <th>Activity Cycle</th>
                      <th>Activity Status</th>
                      <th>Review Status</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                )}

                <tbody>
                  {!activitys_loading ? (
                    activitys?.map((single, index) => {
                      const isOverdue =
                        single.activity_status?.name !== 'COMPLETED' &&
                        single.activity_status?.name !== 'ON-HOLD' &&
                        moment().diff(moment(single.due_time).utc(), 'hours') >
                          0;

                      const isEditing = editId && editId === single._id;

                      return (
                        <tr
                          key={single._id}
                          className={`enhanced-table-row ${
                            isOverdue ? 'overdue' : ''
                          } ${isEditing ? 'editing' : ''}`}
                        >
                          <td>{index + 1 + (page - 1) * LIMIT}</td>

                          {/* Task Name */}
                          <td className='enhanced-editable-cell'>
                            {isEditing && editField === 'task_name' ? (
                              <div className='enhanced-edit-container'>
                                {loadingActivityStatus && reloadData ? (
                                  <span className='enhanced-saving'>
                                    SAVING...
                                  </span>
                                ) : (
                                  <>
                                    <input
                                      className='enhanced-edit-input'
                                      value={newValues?.task_name || ''}
                                      onChange={(e) =>
                                        setNewValues({
                                          task_name: e.target.value,
                                        })
                                      }
                                    />
                                    <div className='enhanced-edit-actions'>
                                      <button
                                        className='enhanced-save-btn'
                                        onClick={() =>
                                          updateStatus(single._id, newValues)
                                        }
                                      >
                                        <i className='fa fa-save'></i>
                                      </button>
                                      <button
                                        className='enhanced-cancel-btn'
                                        onClick={() => setEditId(null)}
                                      >
                                        <i className='fa fa-times'></i>
                                      </button>
                                    </div>
                                  </>
                                )}
                              </div>
                            ) : (
                              <div className='enhanced-cell-content'>
                                <span>{single.task_name}</span>
                                <button
                                  className='enhanced-edit-trigger'
                                  onClick={() => {
                                    setEditField('task_name');
                                    setEditId(single._id);
                                    setNewValues({
                                      task_name: single.task_name,
                                    });
                                  }}
                                >
                                  <i className='fa fa-edit'></i>
                                </button>
                              </div>
                            )}
                          </td>

                          {/* Assigned To */}
                          <td className='enhanced-editable-cell'>
                            {isEditing && editField === 'assigned_to' ? (
                              <div className='enhanced-edit-container'>
                                {loadingActivityStatus && reloadData ? (
                                  <span className='enhanced-saving'>
                                    SAVING...
                                  </span>
                                ) : (
                                  <>
                                    <select
                                      className='enhanced-edit-select'
                                      onChange={(e) =>
                                        setNewValues({
                                          assigned_to: e.target.value,
                                        })
                                      }
                                    >
                                      {dropdownOptions?.assigned_to?.map(
                                        (item) => (
                                          <option
                                            key={item.value}
                                            value={item.value}
                                            selected={
                                              single.assigned_to?._id ===
                                              item.value
                                            }
                                          >
                                            {item.label}
                                          </option>
                                        )
                                      )}
                                    </select>
                                    <div className='enhanced-edit-actions'>
                                      <button
                                        className='enhanced-save-btn'
                                        onClick={() =>
                                          updateStatus(single._id, newValues)
                                        }
                                      >
                                        <i className='fa fa-save'></i>
                                      </button>
                                      <button
                                        className='enhanced-cancel-btn'
                                        onClick={() => setEditId(null)}
                                      >
                                        <i className='fa fa-times'></i>
                                      </button>
                                    </div>
                                  </>
                                )}
                              </div>
                            ) : (
                              <div className='enhanced-cell-content'>
                                <span>{single.assigned_to?.name}</span>
                                <button
                                  className='enhanced-edit-trigger'
                                  onClick={() => {
                                    setEditField('assigned_to');
                                    setEditId(single._id);
                                  }}
                                >
                                  <i className='fa fa-edit'></i>
                                </button>
                              </div>
                            )}
                          </td>

                          {/* Due Time */}
                          <td className='enhanced-editable-cell'>
                            {isEditing && editField === 'due_time' ? (
                              <div className='enhanced-edit-container'>
                                {loadingActivityStatus && reloadData ? (
                                  <span className='enhanced-saving'>
                                    SAVING...
                                  </span>
                                ) : (
                                  <>
                                    <input
                                      className='enhanced-edit-input'
                                      type='datetime-local'
                                      value={newValues?.due_time || ''}
                                      onChange={(e) =>
                                        setNewValues({
                                          due_time: e.target.value,
                                        })
                                      }
                                    />
                                    <div className='enhanced-edit-actions'>
                                      <button
                                        className='enhanced-save-btn'
                                        onClick={() =>
                                          updateStatus(single._id, newValues)
                                        }
                                      >
                                        <i className='fa fa-save'></i>
                                      </button>
                                      <button
                                        className='enhanced-cancel-btn'
                                        onClick={() => setEditId(null)}
                                      >
                                        <i className='fa fa-times'></i>
                                      </button>
                                    </div>
                                  </>
                                )}
                              </div>
                            ) : (
                              <div className='enhanced-cell-content'>
                                <div>
                                  <span>
                                    {single.due_time &&
                                      moment(single.due_time)
                                        .utc()
                                        .format('DD-MM-YYYY hh:mm A')}
                                  </span>
                                  {isOverdue && (
                                    <span className='enhanced-overdue-badge'>
                                      Over Due Task
                                    </span>
                                  )}
                                </div>
                                <button
                                  className='enhanced-edit-trigger'
                                  onClick={() => {
                                    setEditField('due_time');
                                    setEditId(single._id);
                                    setNewValues({ due_time: single.due_time });
                                  }}
                                >
                                  <i className='fa fa-edit'></i>
                                </button>
                              </div>
                            )}
                          </td>

                          {/* Continue with other editable fields following the same pattern... */}
                          {/* For brevity, I'll show the pattern for estimated_time and then continue with non-editable display */}

                          {/* Estimated Time */}
                          <td className='enhanced-editable-cell'>
                            {isEditing && editField === 'estimated_time' ? (
                              <div className='enhanced-edit-container'>
                                {loadingActivityStatus && reloadData ? (
                                  <span className='enhanced-saving'>
                                    SAVING...
                                  </span>
                                ) : (
                                  <>
                                    <input
                                      className='enhanced-edit-input'
                                      value={newValues?.estimated_time || ''}
                                      onChange={(e) =>
                                        setNewValues({
                                          estimated_time: e.target.value,
                                        })
                                      }
                                    />
                                    <div className='enhanced-edit-actions'>
                                      <button
                                        className='enhanced-save-btn'
                                        onClick={() =>
                                          updateStatus(single._id, newValues)
                                        }
                                      >
                                        <i className='fa fa-save'></i>
                                      </button>
                                      <button
                                        className='enhanced-cancel-btn'
                                        onClick={() => setEditId(null)}
                                      >
                                        <i className='fa fa-times'></i>
                                      </button>
                                    </div>
                                  </>
                                )}
                              </div>
                            ) : (
                              <div className='enhanced-cell-content'>
                                <span>{single.estimated_time}</span>
                                <button
                                  className='enhanced-edit-trigger'
                                  onClick={() => {
                                    setEditField('estimated_time');
                                    setEditId(single._id);
                                    setNewValues({
                                      estimated_time: single.estimated_time,
                                    });
                                  }}
                                >
                                  <i className='fa fa-edit'></i>
                                </button>
                              </div>
                            )}
                          </td>

                          {/* Actual Time - Similar pattern */}
                          <td>{single.actual_time}</td>
                          <td>{single.client?.name}</td>
                          <td>{single.process?.name}</td>
                          <td>{single.activity_cycle?.name}</td>
                          <td>
                            <span className='enhanced-status-badge'>
                              {single.activity_status?.name}
                            </span>
                          </td>
                          <td>
                            <span className='enhanced-review-badge'>
                              {single.review_status?.name}
                            </span>
                          </td>

                          <td className='enhanced-actions'>
                            <button
                              className='enhanced-action-btn enhanced-action-view'
                              onClick={() => {
                                setModal(true);
                                setModalData(single);
                                setActivity(single._id);
                              }}
                            >
                              <i className='fa fa-binoculars'></i>
                            </button>
                            <button
                              className='enhanced-action-btn enhanced-action-delete'
                              onClick={() => deleteBtnClicked(single._id)}
                            >
                              <i className='fa fa-trash'></i>
                            </button>
                          </td>
                        </tr>
                      );
                    })
                  ) : (
                    <tr>
                      <td colSpan={12} className='enhanced-loading'>
                        <Spinner />
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            <div className='enhanced-pagination-container'>
              <Pagination
                data={activitys}
                page={page}
                pages={pages}
                count={total_activitys}
                setPage={setPageNumber}
                loading={activitys_loading}
              />
            </div>
          </div>
        </div>
      </div>

      <style jsx>{`
        .enhanced-activity-container {
          padding: 20px;
          background-color: #f8f9fa;
          min-height: 100vh;
        }

        .enhanced-activity-content {
          max-width: 1400px;
          margin: 0 auto;
        }

        .enhanced-activity-card {
          background: white;
          border-radius: 12px;
          box-shadow: rgb(227 233 243) 0px 4px 22px;
          overflow: hidden;
        }

        .enhanced-status-filters {
          display: flex;
          justify-content: space-between;
          align-items: center;
          padding: 20px 25px;
          flex-wrap: wrap;
          gap: 15px;
        }

        .enhanced-filter-cards {
          display: flex;
          gap: 15px;
          flex-wrap: wrap;
        }

        .enhanced-status-card {
          background: white;
          border: 1px solid #e9ecef;
          border-radius: 8px;
          transition: all 0.2s ease;
          cursor: pointer;
        }

        .enhanced-status-card:hover {
          transform: translateY(-2px);
          box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
        }

        .enhanced-status-card-body {
          padding: 12px 16px;
        }

        .enhanced-status-title {
          margin: 0;
          font-size: 13px;
          font-weight: 600;
        }

        .enhanced-status-title a {
          color: #495057;
          text-decoration: none;
        }

        .enhanced-status-title a:hover {
          color: #007bff;
        }

        .enhanced-divider {
          height: 1px;
          background: #e9ecef;
          margin: 0 25px;
        }

        .enhanced-action-bar {
          display: flex;
          justify-content: space-between;
          align-items: center;
          padding: 20px 25px;
          flex-wrap: wrap;
          gap: 15px;
        }

        .enhanced-time-metrics {
          display: flex;
          gap: 15px;
          flex-wrap: wrap;
        }

        .enhanced-metric-card {
          background: #f8f9fa;
          border: 1px solid #e9ecef;
          border-radius: 8px;
          min-width: 160px;
        }

        .enhanced-metric-body {
          padding: 12px 16px;
          text-align: center;
        }

        .enhanced-metric-title {
          margin: 0 0 8px 0;
          font-size: 12px;
          font-weight: 600;
          color: #6c757d;
          text-transform: uppercase;
        }

        .enhanced-metric-value {
          font-size: 16px;
          font-weight: 700;
          color: #495057;
        }

        .enhanced-btn {
          padding: 10px 16px;
          border-radius: 8px;
          text-decoration: none;
          font-weight: 500;
          font-size: 13px;
          transition: all 0.2s ease;
          border: none;
          cursor: pointer;
          display: inline-flex;
          align-items: center;
        }

        .enhanced-btn-danger {
          background: #dc3545;
          color: white;
        }

        .enhanced-btn-danger:hover {
          background: #c82333;
          transform: translateY(-1px);
          color: white;
        }

        .enhanced-btn-secondary {
          background: #6c757d;
          color: white;
        }

        .enhanced-btn-secondary:hover {
          background: #5a6268;
          transform: translateY(-1px);
          color: white;
        }

        .enhanced-records-info {
          padding: 15px 25px;
          background: #f8f9fa;
          border-top: 1px solid #e9ecef;
          font-size: 13px;
          color: #6c757d;
          font-weight: 500;
        }

        .enhanced-table-container {
          overflow-x: auto;
          max-height: 600px;
        }

        .enhanced-table {
          width: 100%;
          border-collapse: collapse;
          font-size: 12px;
        }

        .enhanced-table-header {
          background: #f8f9fa;
          position: sticky;
          top: 0;
          z-index: 10;
        }

        .enhanced-table-header th {
          padding: 14px 12px;
          text-align: left;
          font-weight: 600;
          color: #495057;
          border-bottom: 2px solid #dee2e6;
          font-size: 11px;
          text-transform: uppercase;
          letter-spacing: 0.5px;
        }

        .enhanced-table-row {
          transition: all 0.2s ease;
        }

        .enhanced-table-row:nth-child(even) {
          background-color: #f8f9fa;
        }

        .enhanced-table-row:hover {
          background-color: #e3f2fd;
        }

        .enhanced-table-row.overdue {
          background-color: #ffebee;
        }

        .enhanced-table-row.overdue:hover {
          background-color: #ffcdd2;
        }

        .enhanced-table-row.editing {
          background-color: #fff3cd;
        }

        .enhanced-table td {
          padding: 12px;
          border-bottom: 1px solid #e9ecef;
          vertical-align: middle;
        }

        .enhanced-editable-cell {
          position: relative;
        }

        .enhanced-cell-content {
          display: flex;
          align-items: center;
          justify-content: space-between;
          gap: 8px;
        }

        .enhanced-edit-trigger {
          background: none;
          border: none;
          color: #6c757d;
          cursor: pointer;
          padding: 4px;
          border-radius: 4px;
          transition: all 0.2s ease;
          opacity: 0;
        }

        .enhanced-editable-cell:hover .enhanced-edit-trigger {
          opacity: 1;
        }

        .enhanced-edit-trigger:hover {
          color: #007bff;
          background: #f8f9fa;
        }

        .enhanced-edit-container {
          display: flex;
          flex-direction: column;
          gap: 8px;
        }

        .enhanced-edit-input,
        .enhanced-edit-select {
          padding: 6px 8px;
          border: 1px solid #ced4da;
          border-radius: 4px;
          font-size: 12px;
          width: 100%;
        }

        .enhanced-edit-input:focus,
        .enhanced-edit-select:focus {
          outline: none;
          border-color: #007bff;
          box-shadow: 0 0 0 2px rgba(0, 123, 255, 0.25);
        }

        .enhanced-edit-actions {
          display: flex;
          gap: 4px;
        }

        .enhanced-save-btn,
        .enhanced-cancel-btn {
          padding: 4px 8px;
          border: none;
          border-radius: 4px;
          cursor: pointer;
          font-size: 11px;
          transition: all 0.2s ease;
        }

        .enhanced-save-btn {
          background: #28a745;
          color: white;
        }

        .enhanced-save-btn:hover {
          background: #218838;
        }

        .enhanced-cancel-btn {
          background: #6c757d;
          color: white;
        }

        .enhanced-cancel-btn:hover {
          background: #5a6268;
        }

        .enhanced-saving {
          color: #007bff;
          font-weight: 600;
          font-size: 11px;
        }

        .enhanced-overdue-badge {
          display: inline-block;
          padding: 2px 6px;
          background: #dc3545;
          color: white;
          border-radius: 4px;
          font-size: 10px;
          font-weight: 600;
          margin-left: 8px;
        }

        .enhanced-status-badge,
        .enhanced-review-badge {
          display: inline-block;
          padding: 4px 8px;
          border-radius: 4px;
          font-size: 10px;
          font-weight: 600;
          background: #e9ecef;
          color: #495057;
        }

        .enhanced-actions {
          display: flex;
          gap: 5px;
          justify-content: center;
        }

        .enhanced-action-btn {
          background: none;
          border: 1px solid #dee2e6;
          color: #6c757d;
          cursor: pointer;
          padding: 6px 8px;
          border-radius: 4px;
          transition: all 0.2s ease;
          font-size: 12px;
        }

        .enhanced-action-view:hover {
          background: #007bff;
          color: white;
          border-color: #007bff;
        }

        .enhanced-action-delete:hover {
          background: #dc3545;
          color: white;
          border-color: #dc3545;
        }

        .enhanced-loading {
          text-align: center;
          padding: 40px;
        }

        .enhanced-pagination-container {
          padding: 20px 25px;
          border-top: 1px solid #e9ecef;
          background: #f8f9fa;
        }

        @media (max-width: 768px) {
          .enhanced-status-filters,
          .enhanced-action-bar {
            flex-direction: column;
            align-items: stretch;
          }

          .enhanced-filter-cards,
          .enhanced-time-metrics {
            justify-content: center;
          }

          .enhanced-table {
            font-size: 11px;
          }

          .enhanced-table th,
          .enhanced-table td {
            padding: 8px 6px;
          }
        }
      `}</style>
    </>
  );
};

export default ActivityTableView;
